import type { z } from 'zod'
import { FileFolderType } from '~/prisma/enums'
import type { fileFolderRootTypeSchema } from '~/server/schemas/fileFolder'

export type FileFolderSupportedEntity = 'offer' | 'customer'
export enum FileFolderKey {
  Offer = 'offer',
  Customer = 'customer',
  OfferReport = 'offerReport',
  OfferPreparation = 'offerPreparation',
}

export type FileFolderRootType = z.infer<typeof fileFolderRootTypeSchema>

type FileFolderEntityKey = 'folderId' | 'reportFolderId' | 'preparationFolderId'
export const fileFolderRootTypeConfigs: Record<FileFolderRootType, FileFolderEntityKey> = {
  [FileFolderType.Offer]: 'folderId',
  [FileFolderType.Customer]: 'folderId',
  [FileFolderType.OfferReport]: 'reportFolderId',
  [FileFolderType.OfferPreparation]: 'preparationFolderId',
}

/**
 * Configuration for file folders for different entities.
 */
export const fileFolderEntity = Object.values(FileFolderType).reduce((acc, type) => {
  if (isOfferFolderType(type)) {
    acc[type] = 'offer'
  } else if (isCustomerFolderType(type)) {
    acc[type] = 'customer'
  }
  return acc
}, {} as Record<FileFolderType, FileFolderSupportedEntity>)

/**
 * Configuration for file folders for different keys.
 */
export const fileFolderKey = Object.values(FileFolderType).reduce((acc, type) => {
  if (isOfferReportFolderType(type)) {
    acc[type] = FileFolderKey.OfferReport
  } else if (isOfferPreparationFolderType(type)) {
    acc[type] = FileFolderKey.OfferPreparation
  } else if (isOfferFolderType(type)) {
    acc[type] = FileFolderKey.Offer
  } else if (isCustomerFolderType(type)) {
    acc[type] = FileFolderKey.Customer
  }
  return acc
}, {} as Record<FileFolderType, FileFolderKey>)

/**
 * Configuration for root folder type for different file folder type.
 */
export const fileFolderRootType = Object.values(FileFolderType).reduce((acc, type) => {
  if (isOfferReportFolderType(type)) {
    acc[type] = FileFolderType.OfferReport
  } else if (isOfferPreparationFolderType(type)) {
    acc[type] = FileFolderType.OfferPreparation
  } else if (isOfferFolderType(type)) {
    acc[type] = FileFolderType.Offer
  } else if (isCustomerFolderType(type)) {
    acc[type] = FileFolderType.Customer
  }
  return acc
}, {} as Record<FileFolderType, FileFolderRootType>)

// Helpers
function isOfferReportFolderType(type: FileFolderType): type is Extract<FileFolderType, `OfferReport${string}`> {
  return type.startsWith('OfferReport')
}
function isOfferPreparationFolderType(type: FileFolderType): type is Extract<FileFolderType, `OfferPreparation${string}`> {
  return type.startsWith('OfferPreparation')
}
function isOfferFolderType(type: FileFolderType): type is Extract<FileFolderType, `Offer${string}`> {
  return type.startsWith('Offer')
}
function isCustomerFolderType(type: FileFolderType): type is Extract<FileFolderType, `Customer${string}`> {
  return type.startsWith('Customer')
}
